import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { animate, state, style, transition, trigger } from '@angular/animations'
import { C4PaymentMethod, CancellationInfo, HttpService } from '@api/http.service'
import { catchError, first, tap } from 'rxjs/operators'
import { of } from 'rxjs'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatDialog } from '@angular/material/dialog'
import { Store } from '@ngrx/store'
import { Subscriptions } from '../../../utils/subscriptions'
import { selectProfileState } from '../../../account/user-profile/user-profile.state'
import { OrderElement } from '../../data/orderelement'
import { environment } from 'src/environments/environment'
import { NGXLogger } from 'ngx-logger'
import { DatePipe } from '@angular/common'
import { TranslateService } from '@ngx-translate/core'
import { StatusObject } from '../../../utils/components/interfaces/models'
import { ApiService } from '@api/api.service'
import { ChangePaymentMethodDialogComponent } from '../change-payment-method-dialog/change-payment-method-dialog.component'
import { OrderPaymentMethodActions } from '../../states/payment-methods/payment-methods.actions'
import { MatSort } from '@angular/material/sort'
import { MatPaginator } from '@angular/material/paginator'
import { ComplaintFormComponent } from '../complaint-form/complaint-form.component'
import { Release } from '@api/models/CustomerInfoCall'

const getDate = (dateString: string, inverse: boolean = true): Date => {
  if (inverse) {
    const [yyyy, MM, dd] = dateString.split('.')
    return new Date(+yyyy, +MM, +dd)
  } else {
    const [dd, MM, yyyy] = dateString.split('.')
    return new Date(+yyyy, +MM, +dd)
  }
}

@Component({
  selector: 'cover-orders-table[dataSourceSubscr][dataSourceArt]',
  templateUrl: './orders-table.component.html',
  styleUrls: ['./orders-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class OrdersTableComponent implements OnDestroy, AfterViewInit {
  @Input() dataSourceArt!: any
  @Input() dataSourceSubscr!: any

  orderStatuses: StatusObject = {
    ACTIVE: '',
    ORDERED: '',
    CANCELLED: '',
    DONE: '',
    OPEN: '',
    INACTIVE: '',
    PENDING: '',
  }
  invoicesCurrentlyDownloading: string[] = []
  articlesCurrentlyDownloading: string[] = []
  promotionsCurrentlyDownloading: number[] = []
  cancellationIsLoading: boolean = false
  isRevocationLoading: boolean = false
  allowChangePaymentMethod: boolean = environment.allowChangePaymentMethod
  hasComplaintFeature: boolean = !!environment.features?.complaint
  hasCancellationFeature: boolean = !!environment.features?.cancellation
  customCancellationLink: string | null = environment.customCancellationLink ?? null
  hasInvoiceDownloadFeature: boolean = !!environment.features?.invoiceDownload
  canRevocate: boolean = environment.features?.revocation
  revocationCustomLink: string | null = environment.revocationCustomLink

  environment = environment
  customerInfo: Omit<CancellationInfo, 'attributeId' | 'attributeType'> | undefined
  c4SessionId: string = ''
  adrGr: string = ''
  paymentMethods: C4PaymentMethod[] = []
  paymentMethods$
  private readonly subscriptions: Subscriptions = new Subscriptions(true)

  sorts?: QueryList<MatSort>
  @ViewChildren(MatSort) set _sorts(sorts: QueryList<MatSort>) {
    if (sorts) {
      this.sorts = sorts

      sorts.first?.sortChange.subscribe(() => {
        this.expandedElement = undefined
        this.cdr.detectChanges()
      })

      sorts.last?.sortChange.subscribe(() => {
        this.expandedElement = undefined
        this.cdr.detectChanges()
      })
    }
  }

  @ViewChild('paginatorSubscr') set _paginatorSubscr(paginator: MatPaginator) {
    if (paginator) {
      this.dataSourceSubscr.paginator = paginator
    }
  }

  @ViewChild('paginatorArt') set _paginatorArt(paginator: MatPaginator) {
    if (paginator) {
      this.dataSourceArt.paginator = paginator
    }
  }

  constructor(
    private readonly http: HttpService,
    private readonly snackbar: MatSnackBar,
    private readonly dialog: MatDialog,
    private readonly store: Store,
    private readonly logger: NGXLogger,
    private readonly datePipe: DatePipe,
    private readonly translate: TranslateService,
    private readonly api: ApiService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.subscriptions.plusOne = this.translate
      .get('ORDERS.STATUS')
      .pipe(
        tap((statusObject: StatusObject) => {
          this.orderStatuses = statusObject
        }),
      )
      .subscribe()
    this.subscriptions.plusOne = this.store
      .select(selectProfileState.customerInformationState)
      .pipe(
        tap(customer => {
          this.customerInfo = {
            customerNr: customer.customerNumber,
            customerSubNr: customer.customerSubNumber,
            email: customer.email,
          }
        }),
      )
      .subscribe()
    this.subscriptions.plusOne = store
      .select(selectProfileState.adrGr)
      .pipe(
        tap(adrGr => {
          this.adrGr = adrGr
        }),
      )
      .subscribe()
    this.subscriptions.plusOne = store
      .select(selectProfileState.c4SessionId)
      .pipe(
        tap(sessionId => {
          this.c4SessionId = sessionId
        }),
      )
      .subscribe()
    this.subscriptions.plusOne = this.store
      .select(selectProfileState.paymentMethodsState)
      .pipe(
        tap(paymentMethods => {
          this.paymentMethods = paymentMethods
        }),
      )
      .subscribe()
    this.paymentMethods$ = this.store.select(selectProfileState.paymentMethodsState)
  }

  ngAfterViewInit(): void {
    if (this.sorts) {
      if (this.sorts.first) {
        this.dataSourceSubscr.sort = this.sorts.first
        this.dataSourceSubscr.sortingDataAccessor = this.customSortingStrategy
      }
      if (this.sorts.last) {
        this.dataSourceArt.sort = this.sorts.last
        this.dataSourceArt.sortingDataAccessor = this.customSortingStrategy
      }
    }
    this.cdr.detectChanges()
  }

  customSortingStrategy(item: OrderElement, property: keyof OrderElement) {
    switch (property) {
      case 'price':
        return Number(item.price.replace('€', ''))
      case 'startDate': {
        if(item.startDate.includes('.')) {
          const [day, month, year] = item.startDate.split('.')
          return new Date(Number(year), Number(month) - 1, Number(day))
        } else {
          const year = item.startDate.substring(0, 4)
          const month = item.startDate.substring(4, 6)
          const day = item.startDate.substring(6, 8)
          return new Date(Number(year), Number(month) - 1, Number(day))
        }
      }
      default:
        return item[property as keyof OrderElement]
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  cancel(element: OrderElement): void {
    this.api
      .getCancellationDialog()
      .pipe(
        first(),
        tap(initiateCancel => {
          if (initiateCancel === 'true') {
            this.cancellationIsLoading = true
            this.api
              .cancelSubscription(element.attributeId, element.type)
              .pipe(
                first(),
                tap(_ => {
                  this.cancellationIsLoading = false
                }),
                catchError(err => {
                  this.cancellationIsLoading = false
                  return of(err)
                }),
              )
              .subscribe()
          }
        }),
      )
      .subscribe()
  }

  revocate(element: OrderElement): void {
    this.api
      .getCancellationDialog()
      .pipe(
        first(),
        tap(initiateCancel => {
          if (initiateCancel === 'true') {
            this.isRevocationLoading = true
            this.api
              .revocateSubscription(element)
              .pipe(
                first(),
                tap(_ => {
                  this.isRevocationLoading = false
                }),
                catchError(err => {
                  this.isRevocationLoading = false
                  return of(err)
                }),
              )
              .subscribe()
          }
        }),
      )
      .subscribe()
  }

  // getPaymentMethod(paymentId: string): Observable<C4PaymentMethod | undefined> {
  //   return this.paymentMethods$.pipe(
  //     map(paymentMethods => {
  //       return paymentMethods.find(
  //         it => it.ZAHLWEG_ID === 'S;BBS;100016629;3;2172415;0', // todo: use it.ZAHLWEG_ID
  //         //it.ZAHLWEG_ID
  //       )
  //     }),
  //   )
  // }

  openPaymentMethods(element: OrderElement): void {
    let paymentMethodsToChoose = this.paymentMethods.filter(it => it.ZAHLWEG_ID !== element.paymentId)
    this.subscriptions.plusOne = this.dialog
      .open(ChangePaymentMethodDialogComponent, {
        data: paymentMethodsToChoose,
      })
      .afterClosed()
      .subscribe(paymentMethodSelected => {
        if (paymentMethodSelected)
          this.store.dispatch(
            OrderPaymentMethodActions.trySaveChanges({
              payload: {
                paymentId: paymentMethodSelected.ZAHLWEG_ID,
                subscriptionId: element.uuid,
                sessionId: this.c4SessionId,
              },
            }),
          )
      })
  }

  downloadInvoices(element: OrderElement): void {
    this.invoicesCurrentlyDownloading.push(element.uuid)
    let attributeIdToDownload = element.type !== 'ORDER' ? element.attributeId : element.uuid
    this.api
      .downloadInvoice(this.c4SessionId, attributeIdToDownload)
      .pipe(
        first(),
        tap(_ => {
          this.invoicesCurrentlyDownloading = this.invoicesCurrentlyDownloading.filter(entry => entry !== element.uuid)
        }),
        catchError(err => {
          this.invoicesCurrentlyDownloading = this.invoicesCurrentlyDownloading.filter(entry => entry !== element.uuid)
          return of(err)
        }),
      )
      .subscribe()
  }

  downloadArticle(downloadUrl: string, promotionIndex?: number, orderUUID?: string): void {
    if (promotionIndex !== undefined) {
      this.promotionsCurrentlyDownloading.push(promotionIndex)
    } else if (orderUUID) {
      this.articlesCurrentlyDownloading.push(orderUUID)
    }
    this.api
      .downloadArticle(downloadUrl)
      .pipe(
        first(),
        tap(_ => {
          if (promotionIndex !== undefined) {
            this.promotionsCurrentlyDownloading = this.promotionsCurrentlyDownloading.filter(
              entry => entry !== promotionIndex,
            )
          } else if (orderUUID) {
            this.articlesCurrentlyDownloading = this.articlesCurrentlyDownloading.filter(entry => entry !== orderUUID)
          }
        }),
        catchError(error => {
          if (promotionIndex !== undefined) {
            this.promotionsCurrentlyDownloading = this.promotionsCurrentlyDownloading.filter(
              entry => entry !== promotionIndex,
            )
          } else if (orderUUID) {
            this.articlesCurrentlyDownloading = this.articlesCurrentlyDownloading.filter(entry => entry !== orderUUID)
          }
          return of(error)
        }),
      )
      .subscribe()
  }

  displayedColumnsSubscription: string[] = [
    'orderNumber',
    'name',
    'startDate',
    'price',
    'status',
    'invoiceEndDate',
    'endDate',
  ]
  displayedColumnsArticle: string[] = ['orderNumber', 'name', 'startDate', 'price', 'status']
  expandedElement: string | null | undefined

  mapPotentialOrderDate(column: string): string {
    if (column !== 'startDate') return column.toUpperCase()
    else return 'ORDERDATE'
  }

  mapSubscriptionsStatus(value: string): string {
    switch (value) {
      case 'CANCELED':
        return this.orderStatuses.CANCELLED
      case 'ACTIVE':
        return this.orderStatuses.ACTIVE
      case 'ORDERED':
        return this.orderStatuses.ORDERED
      case 'INACTIVE':
        return this.orderStatuses.INACTIVE
      case 'PENDING':
        return this.orderStatuses.PENDING
      default:
        return value
    }
  }

  mapArticle(value: string, columnName: string) {
    if (columnName === 'status') {
      switch (value) {
        case 'OPEN':
          return this.orderStatuses.OPEN
        case 'DONE':
          return this.orderStatuses.DONE
        default:
          return value
      }
    } else if (columnName === 'startDate') {
      return this.formatArticleDate(value)
    } else return value
  }

  onOpenDetail(element: any): void {
    if (this.expandedElement === element) this.expandedElement = undefined
    else this.expandedElement = element
  }

  /**
   * Formats date strings like "20231121" to "21.11.2023" for example.
   * Used for OrderElement objects of attributeType "ORDER". Other types already deliver date in dd.MM.yyyy format.
   * @param dateStr e.g.20231121
   * @return e.g. 21.11.2023
   */
  formatArticleDate(dateStr: string): string | null {
    let year = parseInt(dateStr.substring(0, 4))
    let month = parseInt(dateStr.substring(4, 6)) - 1
    let day = parseInt(dateStr.substring(6, 8))

    let date = new Date(year, month, day)
    return this.datePipe.transform(date, 'dd.MM.yyyy')
  }

  makeComplaint(order: any): void {
    this.dialog.open(ComplaintFormComponent, {
      data: order,
    })
  }

  canChangeOrderPaymentMethod(order: OrderElement): boolean {
    const { type, paymentId } = order

    return (
      (type.toUpperCase() === 'SUBSCR' || type.toUpperCase() === 'SUBSCRARC') &&
      !!paymentId &&
      (paymentId[0] === 'F' || paymentId[0] === 'S')
    )
  }

  shouldShowInvoice(element: OrderElement): boolean {
    return element.status === 'CANCELED' || element.status === 'ACTIVE'
  }

  shouldShowNextInvoice(element: OrderElement): boolean {
    const [invoiceEndDate, endDate] = [getDate(element.invoiceEndDate), getDate(element.endDate, false)]
    return invoiceEndDate.getTime() < endDate.getTime()
  }
}
